import * as StateLib from "@/Utils/StateLib";
import { ViewListOption } from "./ViewListOption";
import { VIEW_LIBRARY_OPT_PREFIX } from "@/Utils/Constants";
import {
  enumChartTemplateSet,
  enumHierarchyTypes,
  kPositionBoxStyleRule
} from "@coreapp/constants";
import { QChartTemplate } from "@coreapp/CoreApp/QChartTemplate";

export const BuildViewLibraryOptionList = (set, manager) => {
  const wsMgr = StateLib.GetWSMgr();
  const ctm = wsMgr?.GetChartTemplatesMgr();

  if (!ctm) {
    return [];
  }

  const templates = ctm.GetTemplateList(set);
  const chartHierarchyType = GetChartHierarchyType();

  return templates
    .filter(template => {
      return template.GetHierarchyType() === chartHierarchyType;
    })
    .map(template => {
      const ID = template.GetTemplateID();

      return {
        clickHandler: () => {
          manager.selectedTemplateState.setSelectedId(ID);
        },
        content: <ViewListOption id={ID} set={set} manager={manager} />,
        dataTestId: `${VIEW_LIBRARY_OPT_PREFIX}-${set}-${ID}`,
        value: ID,
        divider: true,
        padding: false
      };
    });
};

const GetChartHierarchyType = () => {
  const doc = StateLib.GetDoc();

  let chartHierarchyType = enumHierarchyTypes.employees;
  if (doc?.IsPositionDocument()) {
    chartHierarchyType = enumHierarchyTypes.positions;
  }

  return chartHierarchyType;
};

export const CanCreateTemplateWithName = name => {
  const wsMgr = StateLib.GetWSMgr();
  const ctm = wsMgr?.GetChartTemplatesMgr();
  return ctm.IsNameUnique(name);
};

export const CreateNewTemplate = (name, set) => {
  const wsMgr = StateLib.GetWSMgr();
  const ctm = wsMgr.GetChartTemplatesMgr();

  const eventTracker = wsMgr.GetUsageTracker();
  eventTracker.ReportEvent("view_library.view.created");
  const chartHierarchyType = GetChartHierarchyType();

  const newTemplate = new QChartTemplate();
  newTemplate.InitTemplate();
  newTemplate.SetHierarchyType(chartHierarchyType);
  const templateRule = newTemplate.AddDefaultRule(87);
  if (chartHierarchyType === enumHierarchyTypes.positions) {
    templateRule.SetBoxStyleID(400, kPositionBoxStyleRule);
  }

  newTemplate.SetTemplateID(ctm.GetUniqueID());
  newTemplate.SetTemplateName(name);
  const library = set === enumChartTemplateSet.sharedtemplates ? "shared" : "user";
  newTemplate.SetTemplateLibrary(library);

  ctm.ReplaceTemplate(null, newTemplate);

  ctm.UpdateContext();
};

export const GetTemplateById = (ID, set) => {
  const wsMgr = StateLib.GetWSMgr();
  const ctm = wsMgr.GetChartTemplatesMgr();
  const list = ctm.GetTemplateList(set);

  return ctm.FindTemplateByID(ID, list);
};

export const MoveSystemTemplateToShared = ID => {
  const wsMgr = StateLib.GetWSMgr();
  const ctm = wsMgr.GetChartTemplatesMgr();
  const curTemplate = GetTemplateById(ID, enumChartTemplateSet.systemtemplates);

  const dupTemplate = curTemplate.Clone();
  dupTemplate.SetTemplateName(ctm.GetUniqueName());
  dupTemplate.SetTemplateID(ctm.GetUniqueName());
  dupTemplate.SetTemplateLibrary("shared");
  dupTemplate.SetDirty(true);
  ctm.SwitchTemplates(enumChartTemplateSet.sharedtemplates);
  ctm.ReplaceTemplate(null, dupTemplate);

  ctm.UpdateContext();
};

export const RemoveView = (ID, set) => {
  const wsMgr = StateLib.GetWSMgr();
  const ctm = wsMgr.GetChartTemplatesMgr();
  const curTemplate = GetTemplateById(ID, set);

  ctm.RemoveTemplate(curTemplate);
  ctm.SyncTemplateSet();

  const doc = wsMgr.GetDoc();
  if (doc) {
    const viewMgr = doc.GetViewMgr();
    const view = viewMgr.GetViewByID(ID);
    viewMgr.RemoveView(view);
  }

  ctm.UpdateContext();
  const eventTracker = wsMgr.GetUsageTracker();
  eventTracker.ReportEvent("view_library.view.deleted");
};

export const RenameView = (ID, set, newName) => {
  const wsMgr = StateLib.GetWSMgr();
  const ctm = wsMgr.GetChartTemplatesMgr();
  const curTemplate = GetTemplateById(ID, set);

  // Suppress update if no change was made
  if (curTemplate.GetTemplateName() === newName) {
    return;
  }

  curTemplate.SetTemplateName(newName);
  curTemplate.SetDirty(true);

  ctm.UpdateContext();
};

export const DuplicateView = (ID, set) => {
  const wsMgr = StateLib.GetWSMgr();
  const ctm = wsMgr.GetChartTemplatesMgr();
  const curTemplate = GetTemplateById(ID, set);

  const dupTemplate = curTemplate.Clone();
  const uniqueName = ctm.GetUniqueName(curTemplate.GetTemplateName());
  const uniqueID = ctm.GetUniqueID();
  dupTemplate.SetTemplateName(uniqueName);
  dupTemplate.SetTemplateID(uniqueID);
  dupTemplate.SetDirty(true);
  ctm.ReplaceTemplate(null, dupTemplate);

  ctm.UpdateContext();
};
